/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {{ handle?: string, title?: string, url?: string, image?: string | null, price?: string }} [meta]
   */
  static addProduct(productId, meta = {}) {
    if (!productId) return;

    const records = this.getProductRecords().filter((item) => String(item.id) !== String(productId));
    records.unshift({
      id: String(productId),
      handle: meta.handle || '',
      title: meta.title || '',
      url: meta.url || '',
      image: meta.image || '',
      price: meta.price || '',
    });

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(records.slice(0, this.#MAX_PRODUCTS)));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed product IDs.
   * @returns {string[]} The list of viewed product ids.
   */
  static getProducts() {
    return this.getProductRecords().map((item) => item.id);
  }

  /**
   * Retrieves recently viewed product records for rendering cards.
   * @returns {Array<{ id: string, handle: string, title: string, url: string, image: string, price: string }>}
   */
  static getProductRecords() {
    const raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return [];

    return raw
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return { id: String(item), handle: '', title: '', url: '', image: '', price: '' };
        }
        if (item && item.id) {
          return {
            id: String(item.id),
            handle: item.handle || '',
            title: item.title || '',
            url: item.url || '',
            image: item.image || '',
            price: item.price || '',
          };
        }
        return null;
      })
      .filter(Boolean);
  }
}
